// Electron speech-to-text through OpenAI Realtime WebRTC.
//
// The historical hook name is kept because MicButton already consumes it.
// Audio is not uploaded through Apolo: after authenticated SDP negotiation,
// microphone media flows directly to OpenAI and text deltas arrive live.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getStoredToken } from '../lib/api.js';
import { apiUrl } from '../lib/apiBase.js';
import { RealtimeTranscriber } from '../lib/realtimeTranscriber.js';

function recorderStartError(error) {
  return error?.name === 'NotAllowedError' ? 'not-allowed' : error?.message || 'realtime-failed';
}

function supportsRealtime() {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof globalThis.RTCPeerConnection === 'function');
}

export function useWhisperRecorder({ onTranscript, language = 'pt' } = {}) {
  const supported = supportsRealtime();
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const realtimeRef = useRef(null);

  const cleanup = useCallback(() => {
    realtimeRef.current?.close();
    realtimeRef.current = null;
  }, []);
  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    if (!supported) {
      setError('not-supported');
      return;
    }
    if (realtimeRef.current) return;
    setError(null);
    setConnecting(true);
    let realtime;
    try {
      realtime = new RealtimeTranscriber({
        sessionUrl: apiUrl('/api/voice/realtime/session'),
        token: getStoredToken(),
        language,
        onDelta: (delta) => onTranscript?.(delta),
        onCompleted: (transcript, { hadDelta }) => {
          if (!hadDelta && transcript) onTranscript?.(transcript);
          if (transcript) onTranscript?.(' ');
        },
        onError: (realtimeError) => {
          if (realtimeRef.current !== realtime) return;
          realtime?.close();
          realtimeRef.current = null;
          setError(realtimeError.message || 'realtime-failed');
          setListening(false);
          setBusy(false);
          setConnecting(false);
        },
      });
      realtimeRef.current = realtime;
      await realtime.start();
      if (realtimeRef.current === realtime) setListening(true);
    } catch (startError) {
      realtime?.close();
      if (realtimeRef.current !== realtime) return;
      realtimeRef.current = null;
      setConnecting(false);
      setError(recorderStartError(startError));
    } finally {
      if (realtimeRef.current === realtime) setConnecting(false);
    }
  }, [supported, language, onTranscript]);

  const stop = useCallback(async () => {
    const realtime = realtimeRef.current;
    setListening(false);
    if (!realtime) return;
    setBusy(true);
    try {
      await realtime.stop();
    } catch (stopError) {
      if (realtimeRef.current === realtime) setError(stopError?.message || 'realtime-failed');
    } finally {
      if (realtimeRef.current === realtime) {
        realtimeRef.current = null;
        setBusy(false);
      }
    }
  }, []);

  const abort = useCallback(() => {
    cleanup();
    setListening(false);
    setBusy(false);
    setConnecting(false);
  }, [cleanup]);

  return { listening, busy, connecting, error, supported, start, stop, abort };
}

export function isElectron() {
  if (typeof window === 'undefined') return false;
  if (typeof navigator !== 'undefined' && /Electron/i.test(navigator.userAgent || '')) {
    return true;
  }
  return typeof window.process?.versions?.electron === 'string';
}
